import { Container, useDisclosure } from '@chakra-ui/react';
import {
  ADDRESS_SOURCE_DISPLAY,
  InvoiceSummaryResp,
  NET_TERMS_DISPLAY,
} from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { useRef, useState } from 'react';
import {
  MBox,
  MButton,
  MFlex,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { SELECTION_COL_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../constants/billGroups';
import { useFlags } from '../../../services/launchDarkly';
import { pluralize } from '../../../utils';
import { BillingPageLoadingBar } from '../shared/BillingPageLoadingBar';
import { MassCreditModal } from './MassCreditModal/MassCreditModal';
import { InvoiceManagementMassEmailModal } from './MassEmailModal/InvoiceManagementMassEmailModal';
import { MassVoidModal } from './MassVoidModal/MassVoidModal';
import { useInvoiceManagement } from './useInvoiceManagement';

const INITIAL_SORTING_STATE = [{ id: 'dueDate', desc: false }];

const pinnedLeftColumns = [SELECTION_COL_KEY];

const EMPTY_ARR: InvoiceSummaryResp[] = [];

const FILTER_DATA_DISPLAY_MAP = {
  status: BILL_GROUP_STATUS_DISPLAY,
  netTerms: NET_TERMS_DISPLAY,
  addressSource: ADDRESS_SOURCE_DISPLAY,
};

interface MassActionRows {
  selectedRows: InvoiceSummaryResp[];
  visibleRows: InvoiceSummaryResp[];
}

const NO_MASS_ACTION_ROWS: MassActionRows = {
  selectedRows: EMPTY_ARR,
  visibleRows: EMPTY_ARR,
};

export const InvoiceManagement = () => {
  const { enableInvoiceVoid } = useFlags();
  const {
    open: isMassEmailModalOpen,
    onOpen: onMassEmailModalOpen,
    onClose: onMassEmailModalClose,
  } = useDisclosure();
  const {
    open: isMassCreditModalOpen,
    onOpen: onMassCreditModalOpen,
    onClose: onMassCreditModalClose,
  } = useDisclosure();
  const {
    open: isMassVoidModalOpen,
    onOpen: onMassVoidModalOpen,
    onClose: onMassVoidModalClose,
  } = useDisclosure();
  const [selectedCount, setSelectedCount] = useState(0);
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  /**
   * Snapshotted when a mass-action modal opens rather than read inline: the
   * ref getters return a fresh array on every call, and `useMassInvoiceAction`
   * memoizes on array identity.
   */
  const [massActionRows, setMassActionRows] =
    useState<MassActionRows>(NO_MASS_ACTION_ROWS);
  const tableRef = useRef<DataTableRef>(null);
  const isAnyModalOpen =
    isMassEmailModalOpen || isMassCreditModalOpen || isMassVoidModalOpen;

  const openMassActionModal = (openModal: () => void) => {
    setMassActionRows({
      selectedRows:
        tableRef.current?.getSelectedRows<InvoiceSummaryResp>() ?? EMPTY_ARR,
      visibleRows:
        tableRef.current?.getVisibleRows<InvoiceSummaryResp>() ?? EMPTY_ARR,
    });
    openModal();
  };

  const { customFieldQueryResult, invoiceQueryResult, columns, progress } =
    useInvoiceManagement();

  const {
    data: billGroups,
    isLoading,
    isRefetching,
    refetch,
  } = invoiceQueryResult;

  const { isLoading: isLoadingCustomFields } = customFieldQueryResult;

  return (
    <>
      {isMassEmailModalOpen && (
        <InvoiceManagementMassEmailModal
          selectedRows={massActionRows.selectedRows}
          visibleRows={massActionRows.visibleRows}
          onClose={() => {
            refetch();
            onMassEmailModalClose();
          }}
        />
      )}

      {isMassCreditModalOpen && (
        <MassCreditModal
          selectedRows={massActionRows.selectedRows}
          visibleRows={massActionRows.visibleRows}
          onClose={() => {
            refetch();
            onMassCreditModalClose();
          }}
        />
      )}

      {enableInvoiceVoid && isMassVoidModalOpen && (
        <MassVoidModal
          selectedRows={massActionRows.selectedRows}
          visibleRows={massActionRows.visibleRows}
          onClose={() => {
            refetch();
            onMassVoidModalClose();
            tableRef.current?.resetRowSelection();
          }}
        />
      )}

      <Container
        maxHeight="calc(100vh - 175px)"
        maxWidth="unset"
        px="0"
        position="relative"
        centerContent
        data-testid="bill-run-page"
      >
        <BillingPageLoadingBar
          isVisible={isLoading || isRefetching}
          progress={progress}
        />
        <MPageHeader
          title="Invoice Management"
          subTitle={<MText>Perform actions on unpaid invoices.</MText>}
        />

        {isLoadingCustomFields || isLoading ? (
          <MPageLoader height="50vh" />
        ) : (
          <>
            <MFlex
              mb={2}
              w="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <MFlex alignItems="center" gap={3}>
                <MButton
                  variant="primary"
                  mr={1}
                  onClick={() => openMassActionModal(onMassEmailModalOpen)}
                  isDisabled={selectedCount === 0 || isAnyModalOpen}
                  type="button"
                >
                  Email Selected Invoices
                </MButton>
                <MButton
                  variant="primary"
                  mr={1}
                  onClick={() => openMassActionModal(onMassCreditModalOpen)}
                  isDisabled={selectedCount === 0 || isAnyModalOpen}
                  type="button"
                >
                  Credit Selected Invoices
                </MButton>
                {enableInvoiceVoid && (
                  <MButton
                    variant="primary"
                    mr={1}
                    onClick={() => openMassActionModal(onMassVoidModalOpen)}
                    isDisabled={selectedCount === 0 || isAnyModalOpen}
                    type="button"
                    data-testid="void-selected-invoices-btn"
                  >
                    Void Selected Invoices
                  </MButton>
                )}
                {selectedCount > 0 && (
                  <MText>
                    {formatInteger(selectedCount)}{' '}
                    {pluralize('invoice', selectedCount)} selected
                  </MText>
                )}
              </MFlex>
              <MBox>
                <MButton
                  variant="tertiary"
                  size="sm"
                  onClick={() => tableRef.current?.resetFilters()}
                  type="button"
                >
                  Reset All Filters
                </MButton>
                <MButton
                  variant="tertiary"
                  size="sm"
                  isLoading={isRefetching}
                  onClick={() => refetch()}
                  type="button"
                >
                  Reload
                </MButton>
              </MBox>
            </MFlex>
            <DataTable
              ref={tableRef}
              columns={columns}
              data={billGroups || EMPTY_ARR}
              pinnedLeftColumns={pinnedLeftColumns}
              enablePagination={false}
              filterDataDisplayMap={FILTER_DATA_DISPLAY_MAP}
              enableVirtualization
              clientControlled
              initialSortingState={INITIAL_SORTING_STATE}
              onSelectedRowCountChange={setSelectedCount}
              onVisibleRowCountChange={setVisibleRowCount}
            />
            {billGroups && (
              <MText mt={2} w="100%">
                Showing {formatInteger(visibleRowCount)} of{' '}
                {formatInteger(billGroups.length)} Invoices
              </MText>
            )}
          </>
        )}
      </Container>
    </>
  );
};
